import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const dev = localStorage.getItem("dev_mock_user");
      return dev ? JSON.parse(dev) : null;
    } catch {
      return null;
    }
  });
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    // If dev mock user is set on localhost, preserve it
    const dev = localStorage.getItem("dev_mock_user");
    if (dev) {
      try {
        setUser(JSON.parse(dev));
        setReady(true);
        return JSON.parse(dev);
      } catch {
        // fallback
      }
    }

    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      // Expected when no session — only log unexpected errors
      if (e?.response?.status && e.response.status !== 401) {
        console.error("Auth refresh failed:", e);
      }
      setUser(false);
      return null;
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setDevUser = (mockUser) => {
    if (mockUser) {
      localStorage.setItem("dev_mock_user", JSON.stringify(mockUser));
      setUser(mockUser);
    } else {
      localStorage.removeItem("dev_mock_user");
      localStorage.removeItem("kr_token");
      setUser(false);
    }
  };

  const login = async (email, password) => {
    localStorage.removeItem("dev_mock_user");
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) localStorage.setItem("kr_token", data.access_token);
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    if (data?.verification_required) {
      return {
        verification_required: true,
        email: data.email,
        name: data.name,
        email_sent: data.email_sent !== false,
        manual_verification_code: data.manual_verification_code || "",
        discord_founder_url: data.discord_founder_url || "",
      };
    }
    if (data.access_token) localStorage.setItem("kr_token", data.access_token);
    setUser(data);
    return data;
  };

  const verifyEmail = async (email, otp) => {
    const { data } = await api.post("/auth/verify-email", { email, otp });
    if (data.access_token) localStorage.setItem("kr_token", data.access_token);
    setUser(data);
    return data;
  };

  const resendOtp = async (email) => {
    const { data } = await api.post("/auth/resend-otp", { email });
    return data;
  };

  const logout = async () => {
    localStorage.removeItem("dev_mock_user");
    localStorage.removeItem("kr_token");
    try { await api.post("/auth/logout"); } catch (e) { console.error("Logout request failed:", e); }
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, ready, login, register, verifyEmail, resendOtp, logout, refresh, setDevUser, formatApiError }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
